/**
 * Pagina intermediaria para redirecionamento ao app mobile
 * URL: /app/:slug
 *
 * - Mobile: tenta abrir deep link payflow://tenant/{slug}
 * - Desktop: mostra QR code com o deep link
 * - Fallback: instrucoes para baixar o app
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL || "https://api.cobanq.com.br";

interface TenantInfo {
  id: string;
  nome: string;
  slug: string;
  corPrimaria: string;
  logoUrl?: string;
}

export function AppRedirectPage() {
  const { slug } = useParams<{ slug: string }>();
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [attempted, setAttempted] = useState(false);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const deepLink = `pillarmoney://tenant/${slug}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(deepLink)}`;

  // Resolve tenant info
  useEffect(() => {
    if (!slug) {
      setError("Codigo da empresa nao informado");
      setIsLoading(false);
      return;
    }

    fetch(`${API_URL}/v1/public/tenants/${encodeURIComponent(slug)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data) {
          setTenant(data.data);
        } else {
          setError(data.error?.message || "Empresa nao encontrada");
        }
      })
      .catch(() => setError("Erro ao buscar empresa"))
      .finally(() => setIsLoading(false));
  }, [slug]);

  // Try to open deep link on mobile
  useEffect(() => {
    if (!isMobile || !tenant || attempted) return;

    setAttempted(true);
    window.location.href = deepLink;
  }, [isMobile, tenant, attempted, deepLink]);

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner} />
          <p style={styles.text}>Carregando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>!</div>
          <h2 style={styles.title}>Empresa nao encontrada</h2>
          <p style={styles.text}>{error}</p>
          <p style={styles.textMuted}>
            Verifique o link com a empresa e tente novamente.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={{ ...styles.logo, backgroundColor: tenant?.corPrimaria || "#6D49FF" }}>
          <span style={styles.logoText}>
            {tenant?.nome?.charAt(0).toUpperCase() || "P"}
          </span>
        </div>

        <h1 style={styles.title}>{tenant?.nome}</h1>

        {isMobile ? (
          <>
            <p style={styles.text}>
              Abrindo o app Pillar Money...
            </p>
            <p style={styles.textMuted}>
              Se o app nao abriu automaticamente, instale-o primeiro:
            </p>
            <div style={styles.storeButtons}>
              <a
                href="https://apps.apple.com/app/pillarmoney"
                style={styles.storeButton}
              >
                App Store
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.pillarmoney.app"
                style={styles.storeButton}
              >
                Google Play
              </a>
            </div>
            <p style={styles.textMuted}>
              Apos instalar, acesse este link novamente.
            </p>
          </>
        ) : (
          <>
            <p style={styles.text}>
              Escaneie o QR Code com seu celular para abrir o app:
            </p>
            <img
              src={qrUrl}
              alt={`QR Code para ${tenant?.nome}`}
              width={200}
              height={200}
              style={styles.qrCode}
            />
            <p style={styles.textMuted}>
              Ou digite o codigo <strong>{slug}</strong> diretamente no app.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
    padding: 16,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 40,
    maxWidth: 400,
    width: "100%",
    textAlign: "center",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  },
  logo: {
    width: 72,
    height: 72,
    borderRadius: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },
  logoText: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#fff",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#1a1a1a",
    marginBottom: 8,
  },
  text: {
    fontSize: 16,
    color: "#333",
    marginBottom: 16,
    lineHeight: 1.5,
  },
  textMuted: {
    fontSize: 14,
    color: "#666",
    marginBottom: 16,
    lineHeight: 1.5,
  },
  errorIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: "#f44336",
    color: "#fff",
    fontSize: 28,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
  },
  spinner: {
    width: 40,
    height: 40,
    border: "4px solid #e0e0e0",
    borderTopColor: "#6D49FF",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "0 auto 16px",
  },
  qrCode: {
    margin: "0 auto 16px",
    display: "block",
    borderRadius: 8,
  },
  storeButtons: {
    display: "flex",
    gap: 12,
    justifyContent: "center",
    marginBottom: 16,
  },
  storeButton: {
    display: "inline-block",
    padding: "12px 24px",
    backgroundColor: "#6D49FF",
    color: "#fff",
    borderRadius: 8,
    textDecoration: "none",
    fontWeight: "bold",
    fontSize: 14,
  },
};
